import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Logo, Wordmark } from "@/components/Brand";
import { cx } from "@/lib/format";
import { Theme } from "@/ui/frame/Theme";
export const Route = createFileRoute("/_public")({
  component: PublicLayout,
});

/**
 * The layout for everything a signed-out visitor can reach.
 *
 * # WHAT IT WAS
 *
 * A 384-pixel column centred in an empty page, with the wordmark in one corner
 * and a tagline along the bottom. Nothing wrong with it, and nothing to it:
 * this is the first screen anybody sees of the product and it looked like a
 * password form that had been given a logo.
 *
 * # WHAT IT IS
 *
 * Two halves on a wide screen. The form keeps the left, at the same width it
 * had, because a sign-in form that grows with the window is a sign-in form
 * nobody can aim at. The right is the product saying what it is — the brand
 * gradient, the mark, and three lines that are true rather than three lines of
 * marketing.
 *
 * Below 1024 pixels the right half is gone entirely rather than stacked under
 * the form. Somebody on a phone is signing in; they are not reading a card
 * they have to scroll past the keyboard to see.
 *
 * No marketing copy anywhere: somebody on this page is trying to get into an
 * account, not be sold to. The three lines exist because an empty half-screen
 * looks broken, and because what the product does is a fair thing to say to
 * somebody typing their password into it.
 */
function PublicLayout() {
  return (
    <div className="flex min-h-dvh">
      {/* --- the form ---------------------------------------------------- */}
      <div className="flex min-h-dvh w-full flex-col lg:w-[clamp(28rem,42%,36rem)]">
        <header className="flex items-center justify-between px-6 py-5">
          <Wordmark />
          <Theme />
        </header>

        <main
          id="main"
          className="flex flex-1 items-start justify-center px-6 pb-16 pt-4 sm:items-center sm:pt-0"
        >
          <div className="w-full max-w-md animate-rise">
            <Outlet />
          </div>
        </main>

        <footer className="px-6 py-5 text-center text-[11px] text-[var(--text-muted)]">
          Run your applications. Not your infrastructure.
        </footer>
      </div>

      {/* --- what the product is -----------------------------------------
          # WHY THIS WAS REBUILT RATHER THAN RETINTED
          It was the last room in the product still decorated in the old
          scheme: a cyan-to-violet wash at 7%, a second violet radial on top
          of it, the wordmark in gradient and three blue tick circles. It was
          also the first thing anybody sees, which made it the one screen
          where the product contradicted itself before saying anything.
          The ground is warm graphite now, with one soft light in the corner
          rather than two coloured ones. The claims are numbered in the mono
          face and separated by rules, because three ticks in circles is the
          universal shape of a pricing table and this is not one. */}
      <aside
        className={cx(
          "relative hidden flex-1 overflow-hidden lg:block",
          "border-l border-[var(--border)] bg-[var(--surface-2)]",
        )}
        // Decoration. There is nothing here a screen reader needs that the
        // rest of the page does not already say, and reading three selling
        // points before reaching the password field is a worse experience
        // than not hearing them at all.
        aria-hidden="true"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 620px at 76% 12%, var(--surface), transparent 70%)",
          }}
        />

        <div className="relative flex h-full flex-col justify-center gap-12 px-14 xl:px-20">
          <Logo height={32} />

          {/* The display face at a size the utilities do not carry, because
              this is the one headline in the product. Inline rather than a
              new token: a size used once is a decision about this sentence,
              not a scale step. */}
          <p
            className="t-head max-w-xl"
            style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)", lineHeight: 1.12 }}
          >
            Push a commit.
            <br />
            Get a running service.
          </p>

          <ul className="flex max-w-md flex-col">
            <Claim
              n="01"
              title="Built, deployed, and watched"
              body="A push becomes an image, a release and a rollout. If it goes wrong you are told, with the log that says why."
            />
            <Claim
              n="02"
              title="Isolated by default"
              body="Every workload runs in its own sandbox, on its own network, with nothing reachable that you did not ask for."
            />
            <Claim
              n="03"
              title="Billed by the hour you used"
              body="Rented by the month and paid in advance. One price per plan, nothing owed at any point, and a plan you can change whenever you like."
            />
          </ul>
        </div>
      </aside>
    </div>
  );
}

function Claim({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <li className="flex gap-5 border-t border-[var(--border)] py-4 first:border-t-0 first:pt-0">
      <span className="section-label shrink-0 pt-[3px] tabular-nums">{n}</span>
      <span className="min-w-0">
        <span className="t-strong block">{title}</span>
        <span className="t-body mt-0.5 block">{body}</span>
      </span>
    </li>
  );
}
